/**
 * Formateadores del tablero operativo (AR / ARS). Números en es-AR:
 * coma decimal, punto de miles. Los compactos se usan en ejes y KPIs.
 */

const nfInt = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** `$ 202.975.101` (sin decimales). */
export function fmtARS(n: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(n);
}

/** Compacto para ejes/KPIs: `$ 203,0 M`, `$ 1,5 MM`, `$ 45,0 K`. */
export function fmtARSCompact(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `$ ${nf1.format(n / 1_000_000_000)} MM`;
  if (abs >= 1_000_000) return `$ ${nf1.format(n / 1_000_000)} M`;
  if (abs >= 1_000) return `$ ${nf1.format(n / 1_000)} K`;
  return `$ ${nfInt.format(n)}`;
}

/** Prefijo por moneda: ARS `$`, USD `US$`. */
function simbolo(moneda: string): string {
  return moneda === 'USD' ? 'US$' : '$';
}

/** Moneda completa por código: `$ 202.975.101` / `US$ 145.320`. */
export function fmtMoneda(n: number, moneda: string): string {
  return `${simbolo(moneda)} ${nfInt.format(n)}`;
}

/** Moneda compacta por código: `US$ 1,5 MM`, `$ 203,0 M`. */
export function fmtMonedaCompact(n: number, moneda: string): string {
  const s = simbolo(moneda);
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${s} ${nf1.format(n / 1_000_000_000)} MM`;
  if (abs >= 1_000_000) return `${s} ${nf1.format(n / 1_000_000)} M`;
  if (abs >= 1_000) return `${s} ${nf1.format(n / 1_000)} K`;
  return `${s} ${nfInt.format(n)}`;
}

/** Días promedio: `12,3 días` / `1 día` / `—`. */
export function fmtDias(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const r = Math.round(n * 10) / 10;
  return `${nf1.format(r)} días`;
}

/** Entero con separador de miles: `1.234`. */
export function fmtInt(n: number): string {
  return nfInt.format(n);
}

/** Variación con signo: `+12,3%` / `−5,0%` / `—` si no computable. */
export function fmtVar(actual: number, anterior: number): string {
  if (!Number.isFinite(anterior) || anterior === 0) return '—';
  const pct = ((actual - anterior) / Math.abs(anterior)) * 100;
  const s = nf1.format(Math.abs(pct));
  if (pct > 0) return `+${s}%`;
  if (pct < 0) return `−${s}%`;
  return `0,0%`;
}

/** Signo de la variación: 1 sube, -1 baja, 0 igual/indefinida. */
export function varSign(actual: number, anterior: number): -1 | 0 | 1 {
  if (!Number.isFinite(anterior) || anterior === 0) return 0;
  if (actual > anterior) return 1;
  if (actual < anterior) return -1;
  return 0;
}
